import { parseResponse } from '$lib/client/responseHelpers';

import type { Client } from '@api/functions';
import type { PaymentCard } from '@api/types';
import { Billing, type PaymentMethod, PaymentSource } from '@shared/types';
import type { ProductType } from '@shared/types/invoice';

import { client as defaultClient } from '../client';

export async function getBillingCards(): Promise<PaymentCard[]> {
    const response = await defaultClient.GET('/payment/billing-user-card', {});
    return parseResponse(response);
}

export async function deleteBillingCard(id: string): Promise<void> {
    await defaultClient.DELETE('/payment/billing-user-card/{id}', { params: { path: { id } } });
}

export async function buyProductWithCard(
    offerProductId: string,
    cardId: string,
    paymentSource: PaymentSource,
    isAutoRenew?: boolean,
    fallbackSettingsId?: string
) {
    return defaultClient.POST('/payment/{offerProductId}/{cardId}', {
        params: {
            query: {
                paymentSource,
                isAutoRenew: isAutoRenew ?? false,
                fallbackSettingsId,
            },
            path: { offerProductId, cardId },
        },
        body: {},
    });
}

export async function buyCustomProduct(
    productType: ProductType,
    paymentSource: PaymentSource
) {
    return defaultClient.POST('/payment/custom/{productType}', {
        params: {
            query: {
                paymentSource,
            },
            path: { productType },
        },
        body: {},
    });
}

export async function buyProductWithBillingPage(
    offerProductId: string,
    paymentMethod: PaymentMethod,
    paymentSource: PaymentSource,
    billing?: Billing,
    isAutoRenew?: boolean,
    fallbackSettingsId?: string
) {
    return defaultClient.POST('/payment/{offerProductId}', {
        params: {
            query: {
                paymentMethod,
                paymentSource,
                billing,
                isAutoRenew: isAutoRenew ?? false,
                fallbackSettingsId,
            },
            path: { offerProductId },
        },
        body: {},
    });
}

export async function getInvoicePaymentStats(invoiceId: string, client?: Client) {
    return (client || defaultClient).GET(
        '/stats/invoicePayment/{invoiceId}',
        { params: { path: { invoiceId } } }
    );
}

export async function getSubscription(client?: Client) {
    return (client || defaultClient).GET('/product/user/subscription', {});
}

export async function getTimePackage(client?: Client) {
    return (client || defaultClient).GET('/product/user/time-package', {});
}

export async function cancelSubscription() {
    return defaultClient.DELETE('/payment/subscription', {
        headers: new Headers({
            'Content-Type': 'text/plain',
        }),
    });
}

export async function getBalance(client?: Client) {
    return (client || defaultClient).GET('/payment/user/balance', {});
}

export async function getPaymentMethods() {
    return defaultClient.GET('/payment/method/list', {});
}

export async function getNextBillingForCard(invoiceId: number, client?: Client) {
    return (client || defaultClient).GET('/payment/method/next-payment-data', {
        params: {
            query: {
                invoiceId,
            },
        },
    });
}
